import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  saveWorkingProject,
  setSavingWorkingProject,
  setSavingWorkingProjectError,
  setWorkingProject,
  clearSaveSessionAfterProjectSave,
  updateMostRecentActivity,
} from '..';
import { SaveProject } from '@/connection/projects/save-project';
import { RootState } from '@/store/main';
import { UpdateSession } from '@/connection/sessions/update-session';
import { BrandliveEvent } from '@/types/projects.types';
import {
  clearSaveProjectTimeout,
  getSaveProjectByFieldsTimeout,
  setSaveProjectTimeoutGlobal,
} from './save-project-timeouts';
import sleep from '@/utils/sleep';

const saveProjectMiddleware = createListenerMiddleware();

saveProjectMiddleware.startListening({
  matcher: isAnyOf(saveWorkingProject), // comma separated list of actions to listen for
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(setSavingWorkingProject(true));

    const byFieldstimeout = getSaveProjectByFieldsTimeout();
    // recursive function that waits for the byFieldstimeout to be cleared otherwise we could accidentally wipe out the changes
    if (byFieldstimeout) {
      console.warn('saveWorkingProject called while saveWorkingProjectByFields is still pending');
      // wait for the byFieldstimeout to be cleared
      const checkSaveByFieldsTimeout = async () => {
        const t = getSaveProjectByFieldsTimeout();
        // console.log('byFieldstimeout', t);
        if (!t) return;
        console.warn('waiting for saveWorkingProjectByFields to finish');
        await sleep(100);
        return checkSaveByFieldsTimeout();
      };
      await checkSaveByFieldsTimeout();
    }

    clearSaveProjectTimeout();

    setSaveProjectTimeoutGlobal(
      setTimeout(async () => {
        const state = listenerApi.getState() as RootState;
        const project = state?.project?.workingProject;
        const sessions = state.project?.sessionsToUpdateAfterProjectSave;

        if (project) {
          try {
            const saveProjectResult = await SaveProject(project);
            if (sessions?.length) {
              const promises = sessions.map((session) => UpdateSession(session));
              await Promise.all(promises);
            }

            // this fixes issue where homepage translations do not update in redux when a new item is added into a modules
            if (saveProjectResult?.homepage?.modules && project?.homepage?.modules) {
              const updatedProjectHomepage: BrandliveEvent = {
                ...project,
                homepage: {
                  ...project.homepage,
                  modules: saveProjectResult.homepage.modules,
                },
              };

              listenerApi.dispatch(setWorkingProject(updatedProjectHomepage));
            }

            listenerApi.dispatch(setSavingWorkingProjectError(null));
            listenerApi.dispatch(updateMostRecentActivity({ isPublish: false }));
          } catch (error: unknown) {
            const errMsg = error instanceof Error ? error.message : 'Error saving working project';
            console.error('Error saving project', error);
            listenerApi.dispatch(setSavingWorkingProjectError(errMsg));
          } finally {
            listenerApi.dispatch(setSavingWorkingProject(false));
            listenerApi.dispatch(clearSaveSessionAfterProjectSave());
            clearSaveProjectTimeout();
          }
        }
      }, 5000)
    );
  },
});

export default saveProjectMiddleware;
